import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, X } from "lucide-react";
import { isPushSupported, pushPermission, requestAndSubscribe } from "@/lib/push";
import { toast } from "sonner";

const DISMISS_KEY = "blisson_push_dismissed_at";
const SHOW_AGAIN_DAYS = 14;

export default function PushOptIn() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isPushSupported()) return;
    if (pushPermission() !== "default") return; // already granted or denied
    const last = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (last && Date.now() - last < SHOW_AGAIN_DAYS * 86400_000) return;
    // Show after 8 seconds on first interaction
    const t = setTimeout(() => setOpen(true), 8000);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setOpen(false);
  };

  const allow = async () => {
    setBusy(true);
    try {
      const r = await requestAndSubscribe();
      if (r.ok) {
        toast.success("You'll hear from us only on big drops 🌹");
      } else if (r.reason === "denied") {
        toast.message("No worries — you can enable later from browser settings.");
      } else {
        toast.error("Notifications aren't supported on this browser.");
      }
    } catch (e) {
      toast.error("Could not enable notifications");
    } finally {
      setBusy(false);
      dismiss();
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", damping: 20, stiffness: 180 }}
          className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[55] w-[92vw] max-w-md"
          data-testid="push-optin"
        >
          <div className="bg-white border border-[#C9A961]/40 shadow-2xl p-5 flex items-start gap-4">
            <div className="w-11 h-11 shrink-0 rounded-full bg-[#F8F5F0] flex items-center justify-center">
              <Bell size={18} strokeWidth={1.4} className="text-[#C9A961]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="overline text-[10px] mb-1">Stay in the know</div>
              <div className="font-serif-display text-lg leading-snug">
                Get a heads-up on launches and big drops?
              </div>
              <div className="text-xs text-[#5C5C5C] mt-1">
                Only the important stuff — no spam, promise.
              </div>
              <div className="flex items-center gap-2 mt-3">
                <button
                  onClick={allow}
                  disabled={busy}
                  className="px-4 py-2 bg-black text-white text-[11px] tracking-[0.22em] uppercase hover:bg-[#C9A961] transition-colors disabled:opacity-50"
                  data-testid="push-optin-allow"
                >
                  {busy ? "Enabling…" : "Notify Me"}
                </button>
                <button
                  onClick={dismiss}
                  className="px-3 py-2 text-[11px] tracking-[0.22em] uppercase text-[#5C5C5C] hover:text-black"
                  data-testid="push-optin-dismiss"
                >
                  Not now
                </button>
              </div>
            </div>
            <button onClick={dismiss} className="p-1 text-[#5C5C5C] hover:text-black -mt-1" aria-label="Close">
              <X size={16} />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
